import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import { isBetween } from '@/util/number';
import { IconButton, InputAdornment } from '@mui/material';
import type { Column, FilterFn, Row } from '@tanstack/react-table';
import { subDays } from 'date-fns';
import DateRange from '../../DateRange/DateRange';
import { Input } from '../../Form';
import type { ColumnFilterProps } from '../types';
import { ColumnFilterProvider, useColumnFilter } from './ColumnFilterContext';
import { getInputProps } from './util';

function getTimeRange(column: Column<any>) {
  const filter = column.getFilterValue() as (number | null)[];

  if (
    Array.isArray(filter) &&
    typeof filter[0] === 'number' &&
    typeof filter[1] === 'number'
  ) {
    return { from: new Date(filter[0]), to: new Date(filter[1]) };
  }
  return { from: subDays(new Date(), 7), to: new Date() };
}

export const DateRangeColumnFilter = ({
  column,
  layout,
}: ColumnFilterProps) => {
  const { from, to } = getTimeRange(column);

  return (
    <ColumnFilterProvider column={column} layout={layout}>
      <DateRange
        from={from}
        to={to}
        onChange={(value) => {
          column.setFilterValue([value.from.getTime(), value.to.getTime()]);
        }}
        Trigger={Trigger}
      />
    </ColumnFilterProvider>
  );
};

const Trigger = ({ onClick }: PopperTriggerProps) => {
  const { column, layout } = useColumnFilter();
  const texts = useTexts();
  const [from, to] = (column?.getFilterValue() as (number | null)[]) ?? [];

  const onClear = () => {
    column?.setFilterValue(undefined);
  };
  const label = column?.columnDef.meta?.filterLabel?.([from, to]) ?? '...';

  return (
    <Input
      value={label}
      onClick={onClick}
      InputProps={{
        endAdornment: (from || to) && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            >
              <Icon name="close" size="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...getInputProps(column, layout, texts)}
    />
  );
};

function getColumnValue(row: Row<any>, columnId: string) {
  const value = row.getValue(columnId);
  if (value instanceof Date) {
    return value;
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }
  return new Date(value);
}

export const dateRangeFilterFn: FilterFn<any> = (
  row: Row<any>,
  columnId: string,
  filterValue: unknown[] = []
) => {
  const [from, to] = filterValue;
  const value = getColumnValue(row, columnId);

  if (typeof from !== 'number' || typeof to !== 'number' || value === null) {
    return false;
  }

  return isBetween({
    value: value.getTime(),
    from,
    to,
  });
};
